import { useState } from "react";

import { useHistory } from "react-router-dom";

import Box from "@/components/Box";
import Circle from "@/components/Circle";
import {
  CMP_BUSINESS_TAB_URL,
  CMP_COMMISSIONS_URL,
  CMP_DASHBOARD_URL,
  CMP_LEARNING_HUB_COURSES_URL,
  CMP_LEARNING_HUB_DASHBOARD_URL,
  CMP_LEARNING_HUB_EVENTS_URL,
  CMP_LEARNING_HUB_VIDEO_GUIDES_URL,
  CMP_LEARNING_HUB_WEBINARS_URL,
  CMP_ORDERS_URL,
  CMP_PATIENTS_CALLBACK_REQUESTS_URL,
  CMP_PATIENTS_MANAGE_PATIENTS_URL,
  CMP_PATIENTS_NEW_RESULTS_URL,
  CMP_PROFILE_URL,
  CMP_SHOP_TAB_URL,
  CMP_STOCK_URL,
  CMP_SUPPORT_URL
} from "@/core/urls";
import useLogOut from "@/hooks/header/useLogOut";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import useUserProfile from "@/hooks/useUserProfile";
import UserIcon from "@/images/user.svg?react";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Menu from "@/tpo/Menu";
import BurgerButton from "@/tpo/header/BurgerButton";
import { CloseButton } from "@/v2/Buttons";

import styles from "../common/BurgerMenu.module.css";

export default function BurgerMenu({ accounts = [], burgerButtonProps, bg: _bg = "dark" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [organisationOpen, setOrganisationOpen] = useState(false);
  const [patientsOpen, setPatientsOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [learningHubOpen, setLearningHubOpen] = useState(false);
  const history = useHistory();
  const { resolvedUrls } = useCmpRelatedUrl();
  const { practitionerProfile } = useUserProfile();
  const showReportsTab = practitionerProfile?.showReportsTab;

  const bg = !menuOpen ? _bg : "#282828";

  const logout = useLogOut();

  const handleLinkClick = (to, newTab = false) => {
    if (newTab) {
      window.open(to, "_blank");
    } else {
      history.push(to);
    }
    setMenuOpen(false);
  };

  const MenuItem = ({ children, onClick, color = "white", ...props }) => (
    <Box
      color={color}
      fontFamily="gilroyMedium"
      fontSize={24}
      lineHeight="150%"
      py={20}
      className={styles.burgerMenuItem}
      cursor="pointer"
      onClick={onClick}
      {...props}
    >
      {children}
    </Box>
  );

  const ExpandableMenuItem = ({
    label,
    isOpen,
    onToggle,
    children,
    collapsableContainerProps,
    leftIcon
  }) => (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        color="white"
        fontFamily="gilroyMedium"
        fontSize={24}
        lineHeight="150%"
        py={20}
        cursor="pointer"
        onClick={onToggle}
        className={styles.burgerMenuExpandableItem}
      >
        <Group color="inherit" alignItems="center" gap={10}>
          {leftIcon}
          {label}
        </Group>
        <ChevronComponent direction={isOpen ? "top" : "bottom"} width={5} fill="white" />
      </Box>
      {isOpen && (
        <Box py={20} {...collapsableContainerProps}>
          {children}
        </Box>
      )}
    </Box>
  );

  const SubMenuItem = ({ label, onClick, active }) => (
    <MenuItem onClick={onClick} py={20} fontSize={16} border={0} className={styles.subMenuItem}>
      {active && <Circle bg="green" size={5} position="relative" left="-15px" top="10px" />}
      {label}
    </MenuItem>
  );

  const mobileBurgerMenuContent = (
    <>
      <ExpandableMenuItem
        label="Accounts"
        isOpen={accountsOpen}
        onToggle={() => setAccountsOpen(!accountsOpen)}
        leftIcon={<UserIcon fill="white" width={20} />}
      >
        {accounts.map(section => (
          <Box key={section.label}>
            <Box color="midGrey" fontFamily="gilroyMedium" fontSize={14} lineHeight="150%" pb={16}>
              {section.label}
            </Box>
            <Box pl={28}>
              {section.links.map(link => (
                <SubMenuItem
                  key={link.label}
                  label={link.label}
                  onClick={() => handleLinkClick(link.href || link.to, link.newTab)}
                  active={link.activeTest?.()}
                />
              ))}
            </Box>
          </Box>
        ))}
      </ExpandableMenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_DASHBOARD_URL])}>
        Dashboard
      </MenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_PROFILE_URL])}>
        Practitioner Profile
      </MenuItem>
      <ExpandableMenuItem
        label="Organisation"
        isOpen={organisationOpen}
        onToggle={() => setOrganisationOpen(!organisationOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Payment"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_BUSINESS_TAB_URL].replace(":tab", "payment"))
          }
        />
        <SubMenuItem
          label="Settings"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_BUSINESS_TAB_URL].replace(":tab", "settings"))
          }
        />
        <SubMenuItem
          label="Team Members"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_BUSINESS_TAB_URL].replace(":tab", "team_members"))
          }
        />
      </ExpandableMenuItem>
      <ExpandableMenuItem
        label="Patients"
        isOpen={patientsOpen}
        onToggle={() => setPatientsOpen(!patientsOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Manage Patients"
          onClick={() => handleLinkClick(resolvedUrls[CMP_PATIENTS_MANAGE_PATIENTS_URL])}
        />
        <SubMenuItem
          label="Callback requests"
          onClick={() => handleLinkClick(resolvedUrls[CMP_PATIENTS_CALLBACK_REQUESTS_URL])}
        />
        <SubMenuItem
          label="New results"
          onClick={() => handleLinkClick(resolvedUrls[CMP_PATIENTS_NEW_RESULTS_URL])}
        />
      </ExpandableMenuItem>
      <ExpandableMenuItem
        label="Shop"
        isOpen={shopOpen}
        onToggle={() => setShopOpen(!shopOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Tests"
          onClick={() => handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "tests"))}
        />
        {showReportsTab && (
          <SubMenuItem
            label="Reports"
            onClick={() =>
              handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "reports"))
            }
          />
        )}
        <SubMenuItem
          label="Supplements"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "supplements"))
          }
        />
        <SubMenuItem
          label="Discounts"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "discounts"))
          }
        />
      </ExpandableMenuItem>
      <ExpandableMenuItem
        label="Orders"
        isOpen={ordersOpen}
        onToggle={() => setOrdersOpen(!ordersOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem label="Direct" onClick={() => handleLinkClick(resolvedUrls[CMP_ORDERS_URL])} />
        <SubMenuItem label="Stock" onClick={() => handleLinkClick(resolvedUrls[CMP_STOCK_URL])} />
      </ExpandableMenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_SUPPORT_URL])}>Support</MenuItem>
      <ExpandableMenuItem
        label="Learning hub"
        isOpen={learningHubOpen}
        onToggle={() => setLearningHubOpen(!learningHubOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Dashboard"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_DASHBOARD_URL])}
        />
        <SubMenuItem
          label="Webinars"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_WEBINARS_URL])}
        />
        <SubMenuItem
          label="Events"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_EVENTS_URL])}
        />
        <SubMenuItem
          label="Courses"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_COURSES_URL])}
        />
        <SubMenuItem
          label="Videos"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_VIDEO_GUIDES_URL])}
        />
      </ExpandableMenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_COMMISSIONS_URL])}>
        Commission
      </MenuItem>
      <MenuItem onClick={logout} color="red" borderBottom="none">
        Log out
      </MenuItem>
    </>
  );

  const burgerMenuContent = (
    <>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_DASHBOARD_URL])}>
        Dashboard
      </MenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_PROFILE_URL])}>
        Practitioner Profile
      </MenuItem>
      <ExpandableMenuItem
        label="Organisation"
        isOpen={organisationOpen}
        onToggle={() => setOrganisationOpen(!organisationOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Payment"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_BUSINESS_TAB_URL].replace(":tab", "payment"))
          }
        />
        <SubMenuItem
          label="Settings"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_BUSINESS_TAB_URL].replace(":tab", "settings"))
          }
        />
        <SubMenuItem
          label="Team Members"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_BUSINESS_TAB_URL].replace(":tab", "team_members"))
          }
        />
      </ExpandableMenuItem>
      <ExpandableMenuItem
        label="Patients"
        isOpen={patientsOpen}
        onToggle={() => setPatientsOpen(!patientsOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Manage Patients"
          onClick={() => handleLinkClick(resolvedUrls[CMP_PATIENTS_MANAGE_PATIENTS_URL])}
        />
        <SubMenuItem
          label="Callback requests"
          onClick={() => handleLinkClick(resolvedUrls[CMP_PATIENTS_CALLBACK_REQUESTS_URL])}
        />
        <SubMenuItem
          label="New results"
          onClick={() => handleLinkClick(resolvedUrls[CMP_PATIENTS_NEW_RESULTS_URL])}
        />
      </ExpandableMenuItem>
      <ExpandableMenuItem
        label="Shop"
        isOpen={shopOpen}
        onToggle={() => setShopOpen(!shopOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Tests"
          onClick={() => handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "tests"))}
        />
        {showReportsTab && (
          <SubMenuItem
            label="Reports"
            onClick={() =>
              handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "reports"))
            }
          />
        )}
        <SubMenuItem
          label="Supplements"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "supplements"))
          }
        />
        <SubMenuItem
          label="Discounts"
          onClick={() =>
            handleLinkClick(resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "discounts"))
          }
        />
      </ExpandableMenuItem>
      <ExpandableMenuItem
        label="Orders"
        isOpen={ordersOpen}
        onToggle={() => setOrdersOpen(!ordersOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem label="Direct" onClick={() => handleLinkClick(resolvedUrls[CMP_ORDERS_URL])} />
        <SubMenuItem label="Stock" onClick={() => handleLinkClick(resolvedUrls[CMP_STOCK_URL])} />
      </ExpandableMenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_SUPPORT_URL])}>Support</MenuItem>
      <ExpandableMenuItem
        label="Learning hub"
        isOpen={learningHubOpen}
        onToggle={() => setLearningHubOpen(!learningHubOpen)}
        collapsableContainerProps={{ pl: 28 }}
      >
        <SubMenuItem
          label="Dashboard"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_DASHBOARD_URL])}
        />
        <SubMenuItem
          label="Webinars"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_WEBINARS_URL])}
        />
        <SubMenuItem
          label="Events"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_EVENTS_URL])}
        />
        <SubMenuItem
          label="Courses"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_COURSES_URL])}
        />
        <SubMenuItem
          label="Videos"
          onClick={() => handleLinkClick(resolvedUrls[CMP_LEARNING_HUB_VIDEO_GUIDES_URL])}
        />
      </ExpandableMenuItem>
      <MenuItem onClick={() => handleLinkClick(resolvedUrls[CMP_COMMISSIONS_URL])}>
        Commission
      </MenuItem>
      <MenuItem onClick={logout} color="red" borderBottom="none">
        Log out
      </MenuItem>
    </>
  );

  return (
    <>
      <Menu
        opened={menuOpen}
        onChange={setMenuOpen}
        trigger="click"
        xOffset={-170}
        fullScreen={[true, true, false]}
      >
        <Menu.Target>
          <BurgerButton
            onClick={() => setMenuOpen(!menuOpen)}
            open={menuOpen}
            {...burgerButtonProps}
            bg={bg}
            borderBottomRightRadius={menuOpen ? 0 : 5}
          />
        </Menu.Target>
        <Menu.Dropdown
          bg={bg}
          borderRadius={10}
          borderTopRightRadius={0}
          minWidth={400}
          maxWidth={600}
          px={40}
          py={40}
          data-component-name="BurgerMenu"
          render={(children, { fullScreen, props }) =>
            fullScreen ? (
              <Box {...props} overflow="auto" borderRadius={0}>
                <Group alignItems="center" justifyContent="flex-end">
                  <CloseButton
                    color="white"
                    onClick={() => setMenuOpen(false)}
                    size={null}
                    mr="-4px"
                  />
                </Group>
                {mobileBurgerMenuContent}
              </Box>
            ) : (
              <Box {...props} overflow="auto" maxHeight="calc(100vh - 140px)">
                {burgerMenuContent}
              </Box>
            )
          }
        >
          {burgerMenuContent}
        </Menu.Dropdown>
      </Menu>
    </>
  );
}
